/**
 * OverallPatternCard Component
 * Phase 3.5 - Cross-section synthesis display (Deep package only)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Layers, Info, Shield } from 'lucide-react';

const SECTION_TITLES = {
  relationships: { tr: 'Kalp ve İlişkiler', en: 'Heart and Relationships', az: 'Ürək və Münasibətlər' },
  mind_decisions: { tr: 'Zihin ve Karar Verme', en: 'Mind and Decision Making', az: 'Zəka və Qərar Vermə' },
  life_energy: { tr: 'Yaşam Enerjisi ve Dayanıklılık', en: 'Life Energy and Resilience', az: 'Həyat Enerjisi və Davamlılıq' },
  career_direction: { tr: 'Kariyer ve Yön', en: 'Career and Direction', az: 'Karyera və İstiqamət' },
  communication_social: { tr: 'İletişim ve Sosyal Güç', en: 'Communication and Social Strength', az: 'Ünsiyyət və Sosial Güc' },
  creativity_intuition: { tr: 'Yaratıcılık ve Sezgi', en: 'Creativity and Intuition', az: 'Yaradıcılıq və Intuisiya' }
};

const CONFIDENCE_LABELS = {
  high: { tr: 'Yüksek Gözlem Güveni', en: 'High Confidence' },
  medium: { tr: 'Orta Gözlem Güveni', en: 'Medium Confidence' },
  low: { tr: 'Sınırlı Gözlem Güveni', en: 'Limited Confidence' }
};

const OverallPatternCard = ({ synthesis, lang = 'tr' }) => {
  if (!synthesis || synthesis.status !== 'generated') {
    return null;
  }
  
  return (
    <motion.div
      className="palm-overall-pattern"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
      data-testid="palm-overall-pattern"
    >
      <div className="overall-pattern-header">
        <Layers size={20} />
        <h3>{lang === 'tr' ? 'Genel Desen' : 'Overall Pattern'}</h3>
        {synthesis.confidence && (
          <span className={`confidence-badge-new confidence-${synthesis.confidence.level}`}>
            <Shield size={12} />
            {CONFIDENCE_LABELS[synthesis.confidence.level]?.[lang] || CONFIDENCE_LABELS[synthesis.confidence.level]?.tr}
          </span>
        )}
      </div>
      
      <div className="overall-pattern-content">
        <p>{synthesis.interpretation}</p>
        
        {synthesis.guidance && (
          <div className="synthesis-guidance">
            <Info size={16} />
            <span>{synthesis.guidance}</span>
          </div>
        )}
        
        {synthesis.source_section_ids?.length > 0 && (
          <div className="synthesis-sources">
            <span className="sources-label">
              {lang === 'tr' ? 'Kaynak bölümler:' : 'Source sections:'}
            </span>
            {synthesis.source_section_ids.map((secId, idx) => (
              <span key={secId} className="source-tag">
                {SECTION_TITLES[secId]?.[lang] || secId}
                {idx < synthesis.source_section_ids.length - 1 && ', '}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

OverallPatternCard.propTypes = {
  synthesis: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    source_section_ids: PropTypes.arrayOf(PropTypes.string),
    source_features: PropTypes.arrayOf(PropTypes.string),
    interpretation: PropTypes.string,
    guidance: PropTypes.string,
    confidence: PropTypes.shape({
      score: PropTypes.number,
      level: PropTypes.string,
      source_count: PropTypes.number
    })
  }),
  lang: PropTypes.string
};

export default OverallPatternCard;
